import { Box, Typography } from "@mui/material";

import { isSmallDesktop } from "@hooks/layout";

import styled, { css } from "styled-components";

export const SecondHeaderContainer = styled.header.attrs({ "data-app-header": "" })`
  width: 100%;
  background-color: #59412e;
  position: fixed;
  /* The support-view bar is fixed above everything. Without this the header
     stays pinned at 0 and sits underneath it — invisible, while the body
     padding that reserves the bar's height leaves an empty strip where the
     header should have been. Zero for every ordinary visitor. */
  top: var(--view-as-banner-h, 0px);
  left: 0;
  right: 0;
  z-index: 100;

  .nav-underline {
    display: none;
  }
`;

export const BurgerMenuSubitems = styled(Box)<{ isMediaPage?: boolean }>`
  display: flex;
  justify-content: ${({ isMediaPage }) => (isMediaPage ? "start" : "center")};
  width: 100%;
  position: relative;

  .nav-underline {
    position: absolute;
    bottom: 0;
    height: 3px;
    background: #fff;
    transition: left 0.3s ease, width 0.3s ease;
  }

  & > div {
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const SecondaryBurgerMenuNavLink = styled(Typography)<{ withBorder?: boolean }>`
  text-decoration: none;
  font-size: 24px;
  letter-spacing: 0.01rem;
  text-transform: uppercase !important;
  line-height: 1.4rem;
  font-family: Raleway;
  cursor: pointer;

  font-weight: 400;

  color: ${({ theme }) => theme.palette.header.textColor};

  svg {
    path {
      fill: ${({ theme }) => theme.palette.header.textColor};
    }
  }

  transition: color 250ms ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.palette.textColor.highlighted};
    path {
      fill: ${({ theme }) => theme.palette.textColor.highlighted};
    }
  }

  ${({ withBorder }) =>
    withBorder && "border: 1px solid #635041 !important; border-radius: 4px !important; padding: 12px 10px"}
`;

export const CopyrightTypography = styled(Typography)`
  color: rgb(199 193 189 / 70%);
  font-family: Raleway;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.225rem;
  letter-spacing: 0.00875rem;
  text-transform: capitalize;
  text-align: center;

  a {
    color: rgb(199 193 189 / 70%);
    font-family: Raleway;
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1.225rem;
    letter-spacing: 0.00875rem;
    text-transform: capitalize;
  }
`;

export const ActiveNavigationWrapper = styled(Box)`
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
`;

export const DropdownTypography = styled(Typography)<{ isActive: boolean }>`
  font-size: 1rem;
  letter-spacing: 0.01rem;
  text-transform: uppercase !important;
  line-height: 135%;
  font-family: Raleway;
  font-weight: ${({ isActive }) => (isActive ? 500 : 400)};

  text-decoration-thickness: 1px;
  text-decoration-skip-ink: auto;
  text-underline-offset: 6px;
  transition: color 250ms ease-in-out;
  color: ${({ isActive, theme }) => (isActive ? theme.palette.secondary.contrastText : theme.palette.header.textColor)};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.palette.secondary.light};
  }

  ${isSmallDesktop} {
    font-size: 1.125rem;
  } ;
`;

export const NavLinkWithoutTo = styled(Typography)`
  font-size: 1.25rem;
  line-height: 135%;
  letter-spacing: 0.01rem;
  font-family: Raleway;
  font-weight: 400;
  cursor: pointer;

  transition: color 250ms ease-in-out;

  display: flex;
  align-items: center;
  gap: 8px;

  svg path {
    transition: fill 250ms ease-in-out;
    fill: ${({ theme }) => theme.palette.textColor.common};
  }

  &:hover {
    color: ${({ theme }) => theme.palette.textColor.highlighted};

    svg path {
      fill: ${({ theme }) => theme.palette.textColor.highlighted};
    }
  }
`;

export const UserActionsStyle = {
  color: "textColor.common",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  fontSize: "1rem !important",
  lineHeight: "100%",

  "&:hover": {
    color: "textColor.highlighted",

    path: {
      fill: "#FFDCA8 !important",
    },
  },
};

export const FavoriteSingleNumberStyles = (size: string) => ({
  width: size,
  height: size,
});

export const FavoriteMultipleNumberStyles = (size: string) => ({
  minWidth: size,
  aspectRatio: "1 / 1",
  padding: "3px",
});

export const LinkWrapper = styled(Box)<{ isNotUppercase?: boolean; isSubItem?: boolean }>`
  a {
    display: flex;
    align-items: center;
    gap: 8px;

    text-underline-offset: 6px;
    font-weight: ${({ isSubItem }) => isSubItem && "300"};
    text-transform: ${({ isNotUppercase }) => (isNotUppercase ? "none" : "uppercase")};
    color: ${({ isSubItem, theme }) => isSubItem && theme.palette.primary.contrastText};
    svg path {
      fill: ${({ isSubItem, theme }) => isSubItem && theme.palette.primary.contrastText};
    }
  }

  ${({ isSubItem, theme }) =>
    !isSubItem &&
    css`
      a.active {
        color: ${theme.palette.primary.contrastText};
        font-weight: 500;

        &:after {
          border-bottom: none;
        }

        svg path {
          fill: ${theme.palette.primary.contrastText};
        }
      }
    `}
`;

export const SubItem = styled(Box)`
  position: relative;
  cursor: pointer;

  a {
    padding: 8px 16px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: all 0.25s ease;

  }

  && p {
    color: rgba(255, 255, 255, 0.5);
    font-size: 13px;
    transition: color 0.25s ease;
    white-space: nowrap;
  }

  &:hover a {
    background: rgba(255, 255, 255, 0.06);
  }

  &:hover p {
    color: rgba(255, 255, 255, 0.8);
  }

  svg {
    width: 14px;
    height: 14px;
    fill: #fff;
  }

  && .active {
    background: rgba(255, 255, 255, 0.1);
    cursor: default;
  }

  && .active p {
    color: #fff;
  }
`;
